// Reporting period helpers shared by the public and admin/officer dashboards.
// A Philippine school year runs June to May: "SY 2026–2027" is June 2026 to May 2027.
const SY_START_MONTH = 5; // June, zero-based
const FIRST_SCHOOL_YEAR = 2025;

export const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Month indexes in school-year order: June ... December, January ... May.
export const schoolYearMonths = Array.from({ length: 12 }, (_, i) => (SY_START_MONTH + i) % 12);

export const getCurrentSchoolYear = (today = new Date()) =>
  today.getMonth() >= SY_START_MONTH ? today.getFullYear() : today.getFullYear() - 1;

export const getReportingSchoolYears = (today = new Date()) => {
  const current = getCurrentSchoolYear(today);
  return Array.from({ length: current - FIRST_SCHOOL_YEAR + 1 }, (_, i) => FIRST_SCHOOL_YEAR + i);
};

export const schoolYearLabel = (startYear) => `SY ${startYear}–${startYear + 1}`;

// Calendar year that a given month (0-11) falls in for a school year starting in `startYear`.
export const calendarYearOf = (startYear, monthIndex) =>
  monthIndex >= SY_START_MONTH ? startYear : startYear + 1;

const isOnOrBefore = ({ month, year }, today) =>
  year * 12 + (month - 1) <= today.getFullYear() * 12 + today.getMonth();

const pad = (n) => String(n).padStart(2, '0');

/**
 * First and last calendar day (YYYY-MM-DD) covered by chronological, contiguous
 * { month, year } periods, or null when the periods have no date restriction.
 */
export function getDateRange(periods) {
  const first = periods[0];
  const last = periods[periods.length - 1];
  if (!first?.year) return null;

  const endMonth = last.month ?? 12;
  const lastDay = new Date(last.year, endMonth, 0).getDate();
  return {
    start: `${first.year}-${pad(first.month ?? 1)}-01`,
    end: `${last.year}-${pad(endMonth)}-${pad(lastDay)}`,
  };
}

/**
 * Turns the dashboard filters into the list of { month, year } periods to query.
 * schoolYear: a start year, or 'all' for every school year (month/year null = no restriction).
 * month: 0-11, the selected calendar month.
 * mode: 'period' for just that month, 'ytd' for the school year's first month through it.
 */
export function getPeriods(schoolYear, month, mode = 'period') {
  if (schoolYear === 'all') return [{ month: null, year: null }];

  const toPeriod = (m) => ({ month: m + 1, year: calendarYearOf(schoolYear, m) });
  if (mode !== 'ytd') return [toPeriod(month)];

  return schoolYearMonths.slice(0, schoolYearMonths.indexOf(month) + 1).map(toPeriod);
}

/**
 * Every month of a school year up to today, preceded by the month before it starts,
 * so June can be compared with May. Chronological { month, year } entries.
 */
export function getTrendPeriods(schoolYear, today = new Date()) {
  const previousMonth = { month: SY_START_MONTH, year: schoolYear };
  const months = schoolYearMonths.map((m) => ({ month: m + 1, year: calendarYearOf(schoolYear, m) }));
  return [previousMonth, ...months].filter((period) => isOnOrBefore(period, today));
}

// The calendar month before a single { month, year } period.
export const getPreviousPeriod = ({ month, year }) =>
  month === 1 ? { month: 12, year: year - 1 } : { month: month - 1, year };

export function getPeriodLabel(schoolYear, month, mode = 'period') {
  if (schoolYear === 'all') return 'All School Years';

  const endYear = calendarYearOf(schoolYear, month);
  const end = `${monthNames[month]} ${endYear}`;
  if (mode !== 'ytd' || month === schoolYearMonths[0]) return end;

  const startMonth = schoolYearMonths[0];
  const startYear = calendarYearOf(schoolYear, startMonth);
  const start = startYear === endYear ? monthNames[startMonth] : `${monthNames[startMonth]} ${startYear}`;
  return `${start} – ${end}`;
}
